import { useState } from 'react';

import { Box, Typography } from '@mui/material';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import { SxProps, Theme } from '@mui/material/styles';
import TextField from '@mui/material/TextField';

import { useFeatureFlags, useLanguage } from '../../hooks';
import { SkipLogicQuestion } from '../../types';
import { InfoAlert } from '../uiComponents/infoAlert';

const customStyles: Record<string, SxProps<Theme>> = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
    my: 1,
  },
  fieldContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
  },
  label: {
    fontSize: 14,
    mb: 1,
  },
  select: {
    '& .MuiSelect-select': {
      fontSize: 14,
    },
  },
  inputsRow: {
    display: 'flex',
    gap: 2,
    alignItems: 'center',
  },
  textField: {
    '& .MuiInputBase-input': {
      fontSize: 14,
    },
  },
  andLabel: {
    fontSize: 14,
  },
  questionSelect: {
    '& .MuiSelect-select': {
      fontSize: 14,
    },
  },
  errorMessage: {
    color: 'error.main',
    fontSize: 12,
    mt: 1,
  },
  placeholderText: {
    color: 'text.secondary',
  },
};

type comparisonType =
  | 'greater'
  | 'greaterOrEqual'
  | 'less'
  | 'lessOrEqual'
  | 'between'
  | 'notBetween'
  | undefined;

type validationType = 'noValidation' | 'numberValidation' | 'numberValidationRelative';

interface NumberValidationProps {
  questionId: string;
  upperValue: string;
  lowerValue: string;
  numberValidation: comparisonType;
  numberValidationRelative?: comparisonType | 'betweenQuestionOffset';
  validationOffset?: string;
  logicQuestions?: SkipLogicQuestion[];
  onNumberValidationChange: (
    questionId: string,
    action: string,
    value: string,
    tableId?: string,
  ) => void;
  tableId?: string;
}

const NumberValidation = (props: NumberValidationProps) => {
  const {
    questionId,
    upperValue,
    lowerValue,
    numberValidation,
    validationOffset = '0',
    logicQuestions = [],
    onNumberValidationChange,
    tableId,
    numberValidationRelative,
  } = props;
  const [typeOfValidation, setTypeOfValidation] = useState(
    numberValidation
      ? 'numberValidation'
      : numberValidationRelative
      ? 'numberValidationRelative'
      : 'noValidation',
  );
  const [comparisonType, setComparisonType] = useState(
    numberValidation ?? numberValidationRelative ?? '',
  );

  const lang = useLanguage();
  const { checkFlag } = useFeatureFlags();
  const isRelativeRangeEnabled = checkFlag('NumberRelativeRangeValidation');

  const questionsToCompare = logicQuestions.filter(
    (question) => question.type === 'number' && question.id !== questionId,
  );

  const validationTypes: { value: validationType; label: string }[] = [
    { value: 'noValidation', label: lang.form_builder.noValidation },
    { value: 'numberValidation', label: lang.form_builder.validationFixedRange },
    ...(isRelativeRangeEnabled
      ? [
          {
            value: 'numberValidationRelative' as validationType,
            label: lang.form_builder.validationRelativeRange,
          },
        ]
      : []),
  ];

  const comparisonTypes = [
    { value: 'greater', label: lang.form_builder.greater },
    { value: 'greaterOrEqual', label: lang.form_builder.greaterOrEqual },
    { value: 'less', label: lang.form_builder.less },
    { value: 'lessOrEqual', label: lang.form_builder.lessOrEqual },
    { value: 'between', label: lang.form_builder.between },
    { value: 'notBetween', label: lang.form_builder.notBetween },
    ...(typeOfValidation === 'numberValidationRelative'
      ? [{ value: 'betweenQuestionOffset', label: lang.form_builder.betweenQuestionOffset }]
      : []),
  ];

  const validationMessage = () => {
    const offsetDisplay =
      validationOffset && validationOffset !== '0'
        ? comparisonType === 'betweenQuestionOffset'
          ? ''
          : Number(validationOffset) > 0
          ? ` ${lang.form_builder.plus} ${Math.abs(Number(validationOffset))}`
          : ` ${lang.form_builder.minus} ${Math.abs(Number(validationOffset))}`
        : '';
    let valueLabel = '';
    switch (comparisonType) {
      case 'greater':
      case 'greaterOrEqual':
        valueLabel = lowerValue;
        break;
      case 'less':
      case 'lessOrEqual':
        valueLabel = upperValue;
        break;
      case 'between':
      case 'notBetween':
        valueLabel = `${lowerValue} ${lang.form_builder.and} ${upperValue}`;
        break;
    }
    const comparisonTypeLabel = comparisonTypes
      .find((ct) => ct.value === comparisonType)
      ?.label?.toLowerCase();
    if (typeOfValidation === 'numberValidation') {
      if (comparisonType === 'notBetween') {
        return `${
          lang.form_builder.theNumberMustNotBe
        } ${lang.form_builder.between.toLowerCase()} ${valueLabel}`;
      }
      return `${lang.form_builder.theNumberMustBe} ${comparisonTypeLabel} ${valueLabel}`;
    }

    const getLowerQuestionName = () => {
      const question = questionsToCompare.find((q) => q.id === lowerValue);
      return question?.name || '';
    };
    const getUpperQuestionName = () => {
      const question = questionsToCompare.find((q) => q.id === upperValue);
      return question?.name || '';
    };
    const offsetSign =
      Number(validationOffset) > 0
        ? lang.form_builder.plus
        : Number(validationOffset) === 0
        ? ''
        : lang.form_builder.minus;

    switch (comparisonType) {
      case 'greater':
      case 'greaterOrEqual':
        valueLabel = getLowerQuestionName();
        break;
      case 'less':
      case 'lessOrEqual':
        valueLabel = getUpperQuestionName();
        break;
      case 'between':
      case 'notBetween':
        valueLabel = `${getLowerQuestionName()} ${lang.form_builder.and} ${getUpperQuestionName()}`;
        break;
    }
    if (comparisonType === 'notBetween') {
      return `${
        lang.form_builder.theNumberMustNotBe
      } ${lang.form_builder.between.toLowerCase()} ${valueLabel}`;
    }
    if (comparisonType === 'betweenQuestionOffset') {
      const lowerQuestionName = getLowerQuestionName();
      const offsetLabel = validationOffset !== '0' ? Math.abs(Number(validationOffset)) : '';
      if (lowerQuestionName === '') {
        return lang.form_builder.theNumberMustBe;
      }
      return `${
        lang.form_builder.theNumberMustBe
      } ${lang.form_builder.between.toLowerCase()} ${lowerQuestionName} ${
        lang.form_builder.and
      } ${lowerQuestionName} ${offsetSign} ${offsetLabel}`;
    }
    if (typeOfValidation === 'numberValidationRelative') {
      return `${lang.form_builder.theNumberMustBe} ${comparisonTypeLabel} ${valueLabel} ${offsetDisplay}`;
    }
    return '';
  };

  const handleComparisonTypeChange = (type: string, value: comparisonType | '') => {
    setComparisonType(value);
    if (value === '') return;
    onNumberValidationChange(questionId, type, value, tableId);
  };

  const handleTypeOfValidationChange = (value: validationType) => {
    const oldTypeOfValidation = typeOfValidation;
    setTypeOfValidation(value);
    if (!value) return;
    if (oldTypeOfValidation !== value) {
      setComparisonType('');
    }
    if (value === 'noValidation') {
      onNumberValidationChange(questionId, 'numberValidation', 'noValidation', tableId);
      return;
    }
    onNumberValidationChange(
      questionId,
      value,
      oldTypeOfValidation !== value ? '' : comparisonType,
      tableId,
    );
  };

  const renderFixedRangeFields = () => {
    const isLessValidation = [
      'less',
      'lessOrEqual',
      'between',
      'notBetween',
      'betweenQuestionOffset',
    ].includes(comparisonType);
    const isGreaterValidation = ['greater', 'greaterOrEqual', 'between', 'notBetween'].includes(
      comparisonType,
    );
    const isBetweenValidation = ['between', 'notBetween'].includes(comparisonType);

    const isEmptySet = () => {
      if (isBetweenValidation && !isNaN(parseFloat(lowerValue)) && !isNaN(parseFloat(upperValue))) {
        return parseFloat(lowerValue) >= parseFloat(upperValue);
      }
      return false;
    };

    return (
      <>
        <Box sx={customStyles.fieldContainer}>
          <Typography sx={customStyles.label}>
            {lang.form_builder.validationComparisonType}
          </Typography>
          <Select
            value={comparisonType}
            size='small'
            fullWidth
            sx={customStyles.select}
            onChange={(e) =>
              handleComparisonTypeChange('numberValidation', e.target.value as comparisonType)
            }
            displayEmpty
          >
            <MenuItem
              value=''
              disabled
            >
              <Typography sx={customStyles.placeholderText}>
                {lang.form_builder.validationSelectComparisonType}
              </Typography>
            </MenuItem>
            {comparisonTypes.map((option) => (
              <MenuItem
                key={option.value}
                value={option.value}
              >
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </Box>

        {comparisonType && (
          <Box sx={customStyles.inputsRow}>
            {(isGreaterValidation || isBetweenValidation) && (
              <TextField
                label={lang.form_builder.minimumValue}
                value={lowerValue || ''}
                size='small'
                type='number'
                sx={customStyles.textField}
                onChange={(e) => {
                  const newValue = e.target.value;
                  if (newValue === '' || !isNaN(Number(newValue))) {
                    onNumberValidationChange(questionId, 'lowerValue', newValue, tableId);
                  }
                }}
                error={isNaN(parseFloat(lowerValue)) || isEmptySet()}
                helperText={
                  isNaN(parseFloat(lowerValue))
                    ? lang.form_builder.validNumberWarning
                    : isEmptySet()
                    ? lang.form_builder.validSetWarning
                    : ''
                }
              />
            )}
            {isBetweenValidation && (
              <Typography sx={customStyles.andLabel}>{lang.form_builder.and}</Typography>
            )}
            {(isLessValidation || isBetweenValidation) && (
              <TextField
                label={lang.form_builder.maximumValue}
                value={upperValue || ''}
                size='small'
                type='number'
                sx={customStyles.textField}
                onChange={(e) => {
                  const newValue = e.target.value;
                  if (newValue === '' || !isNaN(Number(newValue))) {
                    onNumberValidationChange(questionId, 'upperValue', newValue, tableId);
                  }
                }}
                error={isNaN(parseFloat(upperValue)) || isEmptySet()}
                helperText={
                  isNaN(parseFloat(upperValue))
                    ? lang.form_builder.validNumberWarning
                    : isEmptySet()
                    ? lang.form_builder.validSetWarning
                    : ''
                }
              />
            )}
          </Box>
        )}
      </>
    );
  };

  const renderQuestionSelect = (
    label: string,
    value: string | undefined,
    onChange: (value: string) => void,
  ) => {
    if (!questionsToCompare?.length) {
      return (
        <Box sx={customStyles.fieldContainer}>
          <Typography sx={customStyles.label}>{label}</Typography>
          <Select
            value=''
            size='small'
            fullWidth
            disabled
            sx={customStyles.questionSelect}
            displayEmpty
          >
            <MenuItem
              value=''
              disabled
            >
              <Typography sx={customStyles.placeholderText}>
                {lang.form_builder.pickQuestion}
              </Typography>
            </MenuItem>
          </Select>
          <Typography sx={customStyles.errorMessage}>
            {lang.form_builder.noQuestionsAvailable}
          </Typography>
        </Box>
      );
    }

    return (
      <Box sx={customStyles.fieldContainer}>
        <Typography sx={customStyles.label}>{label}</Typography>
        <Select
          value={value ?? ''}
          size='small'
          fullWidth
          sx={customStyles.questionSelect}
          onChange={(e) => onChange(e.target.value)}
          displayEmpty
        >
          <MenuItem
            value=''
            disabled
          >
            <Typography sx={customStyles.placeholderText}>
              {lang.form_builder.pickQuestion}
            </Typography>
          </MenuItem>
          {questionsToCompare.map((question) => (
            <MenuItem
              key={question.id}
              value={question.id}
            >
              {question.name}
            </MenuItem>
          ))}
        </Select>
      </Box>
    );
  };

  const renderRelativeRangeFields = () => {
    const isBetweenValidation = ['between', 'notBetween'].includes(comparisonType);
    const isGreaterOffsetValidation = comparisonType === 'betweenQuestionOffset';
    return (
      <>
        <Box sx={customStyles.fieldContainer}>
          <Typography sx={customStyles.label}>
            {lang.form_builder.validationComparisonType}
          </Typography>
          <Select
            value={comparisonType}
            size='small'
            fullWidth
            sx={customStyles.select}
            onChange={(e) =>
              handleComparisonTypeChange(
                'numberValidationRelative',
                e.target.value as comparisonType,
              )
            }
            displayEmpty
          >
            <MenuItem
              value=''
              disabled
            >
              <Typography sx={customStyles.placeholderText}>
                {lang.form_builder.validationSelectComparisonType}
              </Typography>
            </MenuItem>
            {comparisonTypes.map((option) => (
              <MenuItem
                key={option.value}
                value={option.value}
              >
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </Box>

        {comparisonType && (
          <Box sx={customStyles.fieldContainer}>
            <Box sx={customStyles.inputsRow}>
              {(isBetweenValidation ||
                ['greater', 'greaterOrEqual'].includes(comparisonType) ||
                isGreaterOffsetValidation) &&
                renderQuestionSelect(lang.form_builder.minimumValue, lowerValue, (value) =>
                  onNumberValidationChange(questionId, 'lowerValue', value, tableId),
                )}

              {isBetweenValidation && (
                <Typography sx={customStyles.andLabel}>{lang.form_builder.and}</Typography>
              )}

              {(isBetweenValidation || ['less', 'lessOrEqual'].includes(comparisonType)) &&
                renderQuestionSelect(lang.form_builder.maximumValue, upperValue, (value) =>
                  onNumberValidationChange(questionId, 'upperValue', value, tableId),
                )}
            </Box>

            {!isBetweenValidation && (
              <Box sx={customStyles.fieldContainer}>
                <Typography sx={customStyles.label}>{lang.form_builder.offset}</Typography>
                <TextField
                  type='number'
                  size='small'
                  fullWidth
                  value={validationOffset || '0'}
                  sx={customStyles.textField}
                  onChange={(e) => {
                    const newValue = e.target.value;
                    if (newValue === '' || !isNaN(Number(newValue))) {
                      onNumberValidationChange(questionId, 'validationOffset', newValue, tableId);
                    }
                  }}
                />
              </Box>
            )}
          </Box>
        )}
      </>
    );
  };

  return (
    <Box sx={customStyles.container}>
      <Box sx={customStyles.fieldContainer}>
        <Typography sx={customStyles.label}>{lang.form_builder.validationType}</Typography>
        <Select
          value={typeOfValidation}
          size='small'
          fullWidth
          sx={customStyles.select}
          onChange={(e) => handleTypeOfValidationChange(e.target.value as validationType)}
        >
          {validationTypes.map((type) => (
            <MenuItem
              key={type.value}
              value={type.value}
            >
              {type.label}
            </MenuItem>
          ))}
        </Select>
      </Box>
      {typeOfValidation === 'numberValidation' && renderFixedRangeFields()}
      {typeOfValidation === 'numberValidationRelative' && renderRelativeRangeFields()}

      {comparisonType && typeOfValidation !== 'noValidation' && (
        <InfoAlert message={validationMessage()} />
      )}
    </Box>
  );
};

export { NumberValidation };
